import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getAllLightCompanies } from 'app/api/insuranceCompanyApi';
import { getProductById, getProductsByCompany } from 'app/api/productApi';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dropdown } from 'primereact/dropdown';
import {
  createContract,
  getContractById,
  getContractStructureByProductType,
  getProvisionSheetsForContract,
  sendCiastocnyVyber,
  updateContract,
  updateContractPaymentTemplate,
} from 'app/api/contractApi';
import { getAllPaymentFrequencies } from 'app/api/paymentFrequencyApi';
import { getAllHouseholdTypes } from 'app/api/householdTypeApi';
import { getAllPaymentPayedTypes } from 'app/api/paymentPayedTypeApi';
import { ErrorMessage, Field, FieldArray, Form, Formik, FormikErrors, FormikValues } from 'formik';
import { Button } from 'primereact/button';
import { ToastContext } from 'app/context/ToastContext';
import * as Yup from 'yup';
import { InputNumber } from 'primereact/inputnumber';
import ClientWidget from 'app/component/client-widget';
import { formatFromLocalDate, formatDateFromInstant } from 'app/shared/util/date-utils';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import DateTextInput from 'app/component/date-text-input';
import { Divider } from 'primereact/divider';
import AgentWidget from 'app/component/agent-widget';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { AUTHORITIES } from 'app/config/constants';
import { useAppSelector } from 'app/config/store';
import { getAllContractStates } from 'app/api/contractStateApi';
import { DataTable } from 'primereact/datatable';
import { SortOrder } from 'primereact/api';
import { Column } from 'primereact/column';
import { deleteDocumentById, downloadDocument, uploadDocument } from 'app/api/contractDocumentApi';
import { FileUpload } from 'primereact/fileupload';
import { Dialog } from 'primereact/dialog';
import { TabPanel, TabView } from 'primereact/tabview';
import { Checkbox } from 'primereact/checkbox';
import CreateContractPayments from 'app/main/contract/create-contract-payments';
import PaymentTemplateHistoryDialog from 'app/main/contract/payment-template-history-dialog';
import DocumentPreviewDialog from 'app/component/document-preview-dialog';
import ContractHistoryTable from 'app/main/contract/contract-history-table';
import { getAllOpenDispatchNotes } from 'app/api/dispatchNoteApi';

const REQUIRED_FIELD_RULES: Record<string, any> = {
  concludedBy: Yup.object().required('Toto je povinné pole'),
  paymentFrequency: Yup.object().required('Toto je povinné pole'),
  policyHolder: Yup.object().required('Toto je povinné pole'),
  payment: Yup.number().required('Toto je povinné pole'),
  yearlyPayment: Yup.number().required('Toto je povinné pole'),
  dateOfSignature: Yup.date().required('Toto je povinné pole'),
  proposalNr: Yup.string().required('Toto je povinné pole'),
};

const DRUZSTVO_PRODUCT_TYPE_ID = 12;

const CreateContract = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [searchParamsInitial] = useSearchParams();
  const tempParameters = searchParamsInitial.get('p');

  const { id } = useParams<{ id?: string }>();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { showSuccess, showError } = useContext(ToastContext);
  const [isSaving, setIsSaving] = useState(false);
  const account = useAppSelector(state => state.authentication.account);
  const isAdminOrAssistant = hasAnyAuthority(account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.SUPER_ADMIN, AUTHORITIES.ASSISTANT]);
  const readOnly = !isAdminOrAssistant || searchParams.has('info');
  const isNewContractRoute = location.pathname.includes('new-contract');
  const productEditable = isNewContractRoute;

  const fileUploadRef = useRef(null);

  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentFrequencies, setPaymentFrequencies] = useState([]);
  const [dispatchNotes, setDispatchNotes] = useState([]);
  const [householdTypes, setHouseholdTypes] = useState([]);
  const [paymentPayedTypes, setPaymentPayedTypes] = useState([]);
  const [structure, setStructure] = useState(null);
  const [additionalInsuranceOptions, setAdditionalInsuranceOptions] = useState([]);
  const [contractStates, setContractStates] = useState([]);
  const [prilohy, setPrilohy] = useState([]);
  const [paymentTemplateHistoryShow, setPaymentTemplateHistoryShow] = useState(false);

  const [contract, setContract] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductTypeId, setSelectedProductTypeId] = useState(null);
  const [originalProductId, setOriginalProductId] = useState(null);
  const [productChangeDialogVisible, setProductChangeDialogVisible] = useState(false);
  const [pendingSubmitValues, setPendingSubmitValues] = useState(null);
  const [isSavingCiastocnyVyber, setIsSavingCiastocnyVyber] = useState(false);

  const isAdmin = useAppSelector(state =>
    hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.SUPER_ADMIN]),
  );
  const canViewProvisionSheets = useAppSelector(state =>
    hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.SUPER_ADMIN, AUTHORITIES.AGENT]),
  );

  const [deleteDocument, setDeleteDocument] = useState(null);
  const [deleteDocumentDialog, setDeleteDocumentDialog] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [previewDocumentDialog, setPreviewDocumentDialog] = useState(false);
  const [contractProvisionSheets, setContractProvisionSheets] = useState([]);
  const [provisionSheetsLoading, setProvisionSheetsLoading] = useState(false);

  useEffect(() => {
    getAllOpenDispatchNotes(id).then(data => {
      setDispatchNotes(
        data.data.map(p => ({
          label: p.number + ' (' + p.documentName + ')',
          value: p.number,
        })),
      );
    });

    getAllPaymentFrequencies().then(data => {
      setPaymentFrequencies(
        data.data.map(p => ({
          label: p.name,
          value: p.id,
        })),
      );
    });

    getAllContractStates().then(data => {
      setContractStates(
        data.data.map(p => ({
          label: p.name,
          value: p.id,
        })),
      );
    });

    getAllHouseholdTypes().then(data => {
      setHouseholdTypes(
        data.data.map(p => ({
          label: p.name,
          value: p.id,
        })),
      );
    });

    getAllPaymentPayedTypes().then(data => {
      setPaymentPayedTypes(
        data.data.map(p => ({
          label: p.name,
          value: p.id,
        })),
      );
    });

    getAllLightCompanies().then(data => {
      setInsuranceCompanies(
        data.data.map(company => ({
          label: company.name,
          value: company.id,
        })),
      );
      if (id) {
        getContractById(id)
          .then(pr => {
            if (!pr.data?.additionalInsurances || pr.data?.additionalInsurances?.length === 0) {
              pr.data.additionalInsurances = [{ name: '', description: '' }];
            }

            setSelectedCompany(pr.data?.product?.insuranceCompany?.id);
            setContract(pr.data);
            setPrilohy(pr.data?.documents);
            setLoading(false);
            setSelectedProduct(pr.data?.product?.id);
            setSelectedProductTypeId(pr.data?.product?.productType?.id);
            setOriginalProductId(pr.data?.product?.id);

            onProductSelected(pr.data?.product?.id);
          })
          .catch(e => {
            console.error(e);
            showError('Chyba', e.response?.data?.detail);
          });

        if (canViewProvisionSheets) {
          setProvisionSheetsLoading(true);
          getProvisionSheetsForContract(id)
            .then(r => setContractProvisionSheets(r.data))
            .catch(e => console.error(e))
            .finally(() => setProvisionSheetsLoading(false));
        }
      } else {
        setLoading(false);
      }
    });
  }, [id]);

  useEffect(() => {
    if (!id) {
      setSelectedProduct(null);
      setSelectedProductTypeId(null);
    }

    if (selectedCompany) {
      getProductsByCompany(selectedCompany).then(response => {
        setProducts(response.data.map(p => ({ label: p.name, value: p.id })));
      });
    }
  }, [selectedCompany]);

  const goBack = () => {
    if (location.search?.includes('anniversary')) {
      navigate(`/contract-anniversaries?p=${tempParameters}`);
      return;
    }
    navigate(location.pathname.includes('new-contract') ? `/new-contracts?p=${tempParameters}` : `/contracts?p=${tempParameters}`);
  };

  const handleSubmit = (values: any) => {
    if (id && productEditable && originalProductId && selectedProduct !== originalProductId) {
      setPendingSubmitValues(values);
      setProductChangeDialogVisible(true);
      return;
    }
    performSave(values);
  };

  const performSave = (values: any) => {
    let shouldReload = false;
    if (
      values.payment !== contract?.payment ||
      values.paymentFrequency?.id !== contract?.paymentFrequency?.id ||
      values.insuranceStartAt !== contract?.insuranceStartAt
    ) {
      shouldReload = true;
    }

    if (!values.product || values.product.id !== selectedProduct) {
      values.product = { id: selectedProduct };
    }

    let call;
    if (id) {
      call = updateContract(id, values);
    } else {
      call = createContract(values);
    }

    setIsSaving(true);

    call
      .then(response => {
        showSuccess('Úspech', 'Zmluva úspešne uložená.');
        if (!id) {
          navigate(`/new-contracts?p=${tempParameters}`);
        }
        if (shouldReload) {
          setLoading(true);

          setTimeout(() => {
            setLoading(false);
          }, 100);
        }
        setContract(response.data);
        setOriginalProductId(response.data?.product?.id);
      })
      .catch(error => {
        console.error('Error creating agent:', error);
        showError('Chyba', error.response?.data?.detail);
      })
      .finally(() => setIsSaving(false));
  };

  const confirmProductChangeAndSave = () => {
    setProductChangeDialogVisible(false);
    if (pendingSubmitValues) {
      performSave(pendingSubmitValues);
      setPendingSubmitValues(null);
    }
  };

  const cancelProductChange = () => {
    setProductChangeDialogVisible(false);
    setPendingSubmitValues(null);
  };

  const loadStructure = productTypeId => {
    if (productTypeId) {
      setLoading(true);
      getContractStructureByProductType(productTypeId)
        .then(response => {
          setStructure(response.data);
        })
        .finally(() => setLoading(false));
    }
  };

  const onProductSelected = productId => {
    if (productId) {
      getProductById(productId).then(response => {
        setSelectedProduct(productId);
        setSelectedProductTypeId(response.data?.productType?.id);
        setAdditionalInsuranceOptions(response.data?.additionalInsurances?.map(p => ({ label: p.name, value: p.id })));

        loadStructure(response.data?.productType?.id);
      });
    }
  };

  const showField = name => {
    return structure?.fields?.includes(name);
  };

  const validationSchema = useMemo(() => {
    const activeRules = Object.fromEntries(
      Object.entries(REQUIRED_FIELD_RULES).filter(([key]) => structure?.fields?.includes(key)),
    );
    return Yup.object(activeRules);
  }, [structure]);

  const req = (name: string) => (structure?.fields?.includes(name) && name in REQUIRED_FIELD_RULES ? '*' : '');

  const parseLocalDate = date => {
    if (!date) return null;

    const [year, month, day] = date.split('-').map(Number);
    if (!year || !month || !day) return null;

    return { year, month, day };
  };

  const getFullYearsBetween = (fromDate, toDate) => {
    const from = parseLocalDate(fromDate);
    const to = parseLocalDate(toDate);

    if (!from || !to) return null;

    let years = to.year - from.year;
    if (to.month < from.month || (to.month === from.month && to.day < from.day)) {
      years -= 1;
    }

    return years >= 0 ? years : null;
  };

  const formatValidityYears = years => {
    if (years == null) return '';
    if (years === 1) return '1 rok';
    if (years >= 2 && years <= 4) return `${years} roky`;
    return `${years} rokov`;
  };

  const getDruzstvoValidityYears = values => {
    const productTypeId = values?.product?.productType?.id ?? selectedProductTypeId;

    if (productTypeId !== DRUZSTVO_PRODUCT_TYPE_ID || !values?.insuranceEndAt) {
      return null;
    }

    return getFullYearsBetween(values.zaciatokClenstva ?? values.insuranceStartAt, values.insuranceEndAt);
  };

  const getInsuranceEndAtLabel = values => {
    const base = structure?.insuranceEndAtLabel === 'ukoncenie' ? 'Dátum ukončenia' : 'Dátum konca poistenia';
    const validityYears = getDruzstvoValidityYears(values);

    return validityYears == null ? base : `${base} (${formatValidityYears(validityYears)})`;
  };

  function getYearlyPayment(payment, frequency) {
    if (payment && frequency) {
      if (frequency === 1) {
        return payment;
      }
      if (frequency === 2) {
        return payment * 12;
      }
      if (frequency === 3) {
        return payment * 4;
      }
      if (frequency === 4) {
        return payment * 2;
      }
      if (frequency === 5) {
        return payment;
      }
    }

    return null;
  }

  const showPaymentValidFrom = values => {
    if (values.paymentValidFrom) {
      const inputDate = new Date(values.paymentValidFrom);

      // Get the current date without the time portion
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Compare the input date with today's date
      return inputDate > today;
    }

    return false;
  };

  const onSavePaymentTemplateHistory = values => {
    setIsSaving(true);

    updateContractPaymentTemplate(values?.id, values)
      .then(response => {
        setLoading(true);
        setTimeout(() => {
          setLoading(false);
        }, 200);
        setContract(response.data);
        setPaymentTemplateHistoryShow(false);
        showSuccess('Úspech', 'Predpisy boli úspešne aktualizované.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  // console.log('selected', selectedProduct, products);

  function getPersonalFields(
    values: FormikValues,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
  ) {
    return (
      <>
        <div className="col-12 md:col-6">
          {showField('concludedBy') && (
            <div className="p-field">
              <label htmlFor="concludedBy">Zmluvu uzatvoril{req('concludedBy')}</label>
              <AgentWidget
                id="concludedBy"
                value={values.concludedBy?.id}
                readonly={readOnly}
                onPersonChanged={e => {
                  void setFieldValue('concludedBy.id', e);
                }}
                showCareer={true}
                selectedCareerType={values.careerForConcludedBy}
                onCareerChanged={e => {
                  void setFieldValue('careerForConcludedBy', e);
                }}
              />
              <ErrorMessage name="concludedBy" component="div" className="p-error" />
            </div>
          )}
          {showField('tipster') && (
            <>
              <div className="p-field">
                <label htmlFor="tipster">Tipér</label>
                <AgentWidget
                  id="tipster"
                  isDeletable={true}
                  readonly={readOnly}
                  value={values.tipster?.id}
                  onPersonChanged={e => {
                    void setFieldValue('tipster.id', e);
                  }}
                  showCareer={true}
                  selectedCareerType={values.careerForTipster}
                  onCareerChanged={e => {
                    void setFieldValue('careerForTipster', e);
                  }}
                />
                <ErrorMessage name="tipster" component="div" className="p-error" />
              </div>
              {values.tipster?.id && (
                <div className="p-field">
                  <label htmlFor="provisionForTipster">Provízia pre tipéra (%)</label>
                  <InputNumber
                    id="provisionForTipster"
                    readOnly={readOnly}
                    value={values.provisionForTipster}
                    locale="sk"
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    onValueChange={e => void setFieldValue('provisionForTipster', e.value)}
                  />
                  <ErrorMessage name="provisionForTipster" component="div" className="p-error" />
                </div>
              )}
            </>
          )}
        </div>
        {showField('policyHolder') && (
          <>
            <div className="col-12 md:col-6">
              <div className="p-field">
                <label htmlFor="policyHolder">Poistník{req('policyHolder')}</label>
                <ClientWidget
                  id="policyHolder"
                  readonly={readOnly}
                  value={values.policyHolder?.id}
                  onPersonChanged={e => {
                    void setFieldValue('policyHolder.id', e);
                  }}
                  pripoistenie={showField('additionalInsurances')}
                  insurances={values?.additionalInsurances || []}
                  setInsurances={insurances => void setFieldValue('additionalInsurances', insurances)}
                  additionalInsuranceOptions={additionalInsuranceOptions}
                />
                <ErrorMessage name="policyHolder" component="div" className="p-error" />
              </div>
            </div>
          </>
        )}
        {showField('insuredClients') && (
          <div className="col-12">
            <label htmlFor="insuredClients">Zoznam poistencov</label>
            <FieldArray name="insuredClients">
              {({ insert, remove, push }) => (
                <div className="flex grid">
                  {values.insuredClients?.length > 0 &&
                    values.insuredClients.map((person, index) => {
                      return (
                        <div key={person?.id} className="flex p-mb-2 col-12 md:col-6" style={{ marginBottom: '5px' }}>
                          <ClientWidget
                            id={`insuredPerson.${index}.id`}
                            key={person?.id}
                            readonly={readOnly}
                            value={person?.id}
                            onPersonChanged={e => {
                              const oldClient = values.insuredClients[index]?.id;

                              void setFieldValue(`insuredClients.${index}.id`, e);

                              const newAdditionalInsurances = values?.additionalInsurances?.filter(ai => ai.client?.id !== oldClient);

                              void setFieldValue('additionalInsurances', newAdditionalInsurances);
                            }}
                            pripoistenie={showField('additionalInsurances')}
                            insurances={values?.additionalInsurances}
                            setInsurances={insurances => void setFieldValue('additionalInsurances', insurances)}
                            additionalInsuranceOptions={additionalInsuranceOptions}
                            onDelete={() => {
                              const oldClient = values.insuredClients[index]?.id;
                              const newAdditionalInsurances = values.additionalInsurances?.filter(ai => ai.client?.id !== oldClient);
                              void setFieldValue('additionalInsurances', newAdditionalInsurances);

                              remove(index);
                            }}
                          />

                          {/*<div className="col-4" style={{ alignContent: 'center' }}>*/}
                          {/*  <div className="p-field">*/}
                          {/*    {!readOnly && <Button icon="pi pi-trash" severity="danger" type="button" onClick={() => remove(index)} />}*/}
                          {/*  </div>*/}
                          {/*</div>*/}
                        </div>
                      );
                    })}

                  <div className="col-12 md:col-6" style={{ alignSelf: 'center', marginBottom: '20px' }}>
                    <div className="p-field">
                      {!readOnly && (
                        <Button
                          style={{ backgroundColor: 'green' }}
                          type="button"
                          disabled={values.insuredClients?.find(ic => !ic.id)}
                          label="Pridať poistenca"
                          onClick={() => push({ id: null })}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </FieldArray>
            <div />
          </div>
        )}
      </>
    );
  }

  const onShowHistoriaPlatieb = () => {
    setPaymentTemplateHistoryShow(true);
  };

  function getGeneralFields(
    values: FormikValues,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
  ) {
    return (
      <>
        {showField('contractNr') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="contractNr">Číslo zmluvy</label>
              <Field readOnly={readOnly} name="contractNr" as={InputText} />
              <ErrorMessage name="contractNr" component="div" className="p-error" />
            </div>
          </div>
        )}

        {showField('proposalNr') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="proposalNr">Číslo návrhu{req('proposalNr')}</label>
              <Field readOnly={readOnly} name="proposalNr" as={InputText} />
              <ErrorMessage name="proposalNr" component="div" className="p-error" />
            </div>
          </div>
        )}

        {showField('paymentFrequency') && (
          <div className={'col-12 md:col-6'}>
            <div className="p-field">
              <label htmlFor="paymentFrequency">Frekvencia platenia{req('paymentFrequency')}</label>
              <Dropdown
                id="paymentFrequency"
                name="paymentFrequency"
                disabled={readOnly || (id && [9, 10].includes(values.product?.productType?.id))}
                value={values.paymentFrequency?.id}
                options={paymentFrequencies}
                optionLabel="label"
                onChange={e => {
                  setFieldValue('paymentFrequency.id', e.value).then(() => {
                    const newYearly = getYearlyPayment(values.payment, e.value);

                    if (newYearly) {
                      setFieldValue('yearlyPayment', newYearly);
                    }
                  });
                }}
                placeholder="Vyberte si frekvenciu platenia"
              />
              <ErrorMessage name="paymentFrequency" component="div" className="p-error" />
            </div>
          </div>
        )}
        {showField('payment') && (
          <div className={values?.contractPaymentTemplates?.length > 0 ? 'col-12 md:col-4' : 'col-12 md:col-6'}>
            <div className="p-field">
              <label htmlFor="payment">Výška platby{req('payment')}</label>
              <InputNumber
                id="payment"
                value={values.payment}
                locale="sk"
                readOnly={readOnly}
                minFractionDigits={0}
                maxFractionDigits={2}
                onValueChange={e => {
                  setFieldValue('payment', e.value).then(value => {
                    const newYearly = getYearlyPayment(e.value, values.paymentFrequency?.id);

                    if (newYearly) {
                      setFieldValue('yearlyPayment', newYearly);
                    }
                  });
                }}
              />
              <ErrorMessage name="payment" component="div" className="p-error" />
            </div>
          </div>
        )}
        {showField('payment') && values?.contractPaymentTemplates?.length > 0 && (
          <div className="col-12 md:col-2">
            <div className="p-field">
              <Button style={{ backgroundColor: '#e31818', marginTop: '20px' }} type="button" onClick={onShowHistoriaPlatieb}>
                História predpisov
              </Button>
            </div>
          </div>
        )}
        {(showField('payment') || showField('paymentFrequency')) && showPaymentValidFrom(values) && (
          <div className="col-12">
            <div className="p-field">
              <label htmlFor="payment" style={{ color: 'red' }}>
                Táto kombinácia frekvencie a platby je platná až {formatFromLocalDate(values.paymentValidFrom)}
              </label>
              {/*<InputNumber*/}
              {/*  id="payment"*/}
              {/*  value={values.payment}*/}
              {/*  locale="sk"*/}
              {/*  disabled={readOnly}*/}
              {/*  minFractionDigits={0}*/}
              {/*  maxFractionDigits={2}*/}
              {/*  onValueChange={e => {*/}
              {/*    setFieldValue('payment', e.value).then(value => {*/}
              {/*      const newYearly = getYearlyPayment(e.value, values.paymentFrequency?.id);*/}

              {/*      if (newYearly) {*/}
              {/*        setFieldValue('yearlyPayment', newYearly);*/}
              {/*      }*/}
              {/*    });*/}
              {/*  }}*/}
              {/*/>*/}
            </div>
          </div>
        )}
        {showField('yearlyPayment') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="yearlyPayment">Ročná platba{req('yearlyPayment')}</label>
              <InputNumber
                readOnly={readOnly}
                id="yearlyPayment"
                value={values.yearlyPayment}
                locale="sk"
                minFractionDigits={0}
                maxFractionDigits={2}
                onValueChange={e => void setFieldValue('yearlyPayment', e.value)}
              />
              <ErrorMessage name="yearlyPayment" component="div" className="p-error" />
            </div>
          </div>
        )}
        {showField('dateOfSignature') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="dateOfSignature">Dátum podpisu{req('dateOfSignature')}</label>
              <DateTextInput
                readOnly={readOnly}
                id="dateOfSignature"
                value={values.dateOfSignature}
                onChange={val => void setFieldValue('dateOfSignature', val)}
              />
              <ErrorMessage name="dateOfSignature" component="div" className="p-error" />
            </div>
          </div>
        )}
        {showField('zaciatokClenstva') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="zaciatokClenstva">Začiatok členstva</label>
              <DateTextInput
                readOnly={readOnly || (id && values.zaciatokClenstva)}
                id="zaciatokClenstva"
                value={values.zaciatokClenstva}
                onChange={val => void setFieldValue('zaciatokClenstva', val)}
              />
              <ErrorMessage name="zaciatokClenstva" component="div" className="p-error" />
            </div>
          </div>
        )}
        {showField('insuranceStartAt') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="insuranceStartAt">Dátum začiatku poistenia</label>
              <DateTextInput
                readOnly={readOnly}
                id="insuranceStartAt"
                value={values.insuranceStartAt}
                onChange={val => void setFieldValue('insuranceStartAt', val)}
              />
              <ErrorMessage name="insuranceStartAt" component="div" className="p-error" />
            </div>
          </div>
        )}
        {showField('insuranceEndAt') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="insuranceEndAt">{getInsuranceEndAtLabel(values)}</label>
              <DateTextInput
                readOnly={readOnly}
                id="insuranceEndAt"
                value={values.insuranceEndAt}
                onChange={val => void setFieldValue('insuranceEndAt', val)}
              />
              <ErrorMessage name="insuranceEndAt" component="div" className="p-error" />
            </div>
          </div>
        )}
        {showField('insuranceSum') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="insuranceSum">Celkové poistné</label>
              <InputNumber
                readOnly={readOnly}
                id="insuranceSum"
                value={values.insuranceSum}
                locale="sk"
                minFractionDigits={0}
                maxFractionDigits={2}
                onValueChange={e => void setFieldValue('insuranceSum', e.value)}
              />
              <ErrorMessage name="insuranceSum" component="div" className="p-error" />
            </div>
          </div>
        )}
        {showField('householdType') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="householdType">Typ domácnosti</label>
              <Dropdown
                disabled={readOnly}
                id="householdType"
                name="householdType"
                value={values.householdType?.id}
                options={householdTypes}
                optionLabel="label"
                onChange={e => void setFieldValue('householdType.id', e.value)}
                placeholder="Vyberte si typ domácnosti"
              />
              <ErrorMessage name="householdType" component="div" className="p-error" />
            </div>
          </div>
        )}

        {showField('insuranceSumDomacnost') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="insuranceSumDomacnost">Poistná suma domácnosť</label>
              <InputNumber
                readOnly={readOnly}
                id="insuranceSumDomacnost"
                value={values.insuranceSumDomacnost}
                locale="sk"
                minFractionDigits={0}
                maxFractionDigits={2}
                onValueChange={e => void setFieldValue('insuranceSumDomacnost', e.value)}
              />
              <ErrorMessage name="insuranceSumDomacnost" component="div" className="p-error" />
            </div>
          </div>
        )}
        {showField('insuranceSumNehnutelnost') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="insuranceSumNehnutelnost">Poistná suma nehnuteľnosť</label>
              <InputNumber
                readOnly={readOnly}
                id="insuranceSumNehnutelnost"
                value={values.insuranceSumNehnutelnost}
                locale="sk"
                minFractionDigits={0}
                maxFractionDigits={2}
                onValueChange={e => void setFieldValue('insuranceSumNehnutelnost', e.value)}
              />
              <ErrorMessage name="insuranceSumNehnutelnost" component="div" className="p-error" />
            </div>
          </div>
        )}
        {showField('amountOfLoan') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="amountOfLoan">Výška úveru</label>
              <InputNumber
                readOnly={readOnly}
                id="amountOfLoan"
                value={values.amountOfLoan}
                locale="sk"
                minFractionDigits={0}
                maxFractionDigits={2}
                onValueChange={e => void setFieldValue('amountOfLoan', e.value)}
              />
              <ErrorMessage name="amountOfLoan" component="div" className="p-error" />
            </div>
          </div>
        )}
        {showField('dobaSplatnosti') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="dobaSplatnosti">Doba splatnosti</label>
              <InputNumber
                readOnly={readOnly}
                id="dobaSplatnosti"
                value={values.dobaSplatnosti}
                onValueChange={e => void setFieldValue('dobaSplatnosti', e.value)}
              />
              <ErrorMessage name="dobaSplatnosti" component="div" className="p-error" />
            </div>
          </div>
        )}
        {showField('dobaFixacie') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="dobaFixacie">Doba fixácie</label>
              <InputNumber
                readOnly={readOnly}
                id="dobaFixacie"
                value={values.dobaFixacie}
                locale="sk"
                minFractionDigits={0}
                maxFractionDigits={2}
                onValueChange={e => void setFieldValue('dobaFixacie', e.value)}
              />
              <ErrorMessage name="dobaFixacie" component="div" className="p-error" />
            </div>
          </div>
        )}
        {showField('vyskaPoplatku') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="vyskaPoplatku">Výška poplatku</label>
              <InputNumber
                readOnly={readOnly}
                id="vyskaPoplatku"
                value={values.vyskaPoplatku}
                locale="sk"
                minFractionDigits={0}
                maxFractionDigits={2}
                onValueChange={e => void setFieldValue('vyskaPoplatku', e.value)}
              />
              <ErrorMessage name="vyskaPoplatku" component="div" className="p-error" />
            </div>
          </div>
        )}
        {showField('paymentPayedType') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="paymentPayedType">Poplatok uhradený</label>
              <Dropdown
                disabled={readOnly}
                id="paymentPayedType"
                name="paymentPayedType"
                value={values.paymentPayedType?.id}
                options={paymentPayedTypes}
                optionLabel="label"
                onChange={e => void setFieldValue('paymentPayedType.id', e.value)}
                placeholder="Vyberte si typ úhrady poplatku"
              />
              <ErrorMessage name="paymentPayedType" component="div" className="p-error" />
            </div>
          </div>
        )}
        {showField('goalSum') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="goalSum">Cieľová suma</label>
              <InputNumber
                readOnly={readOnly}
                id="goalSum"
                value={values.goalSum}
                locale="sk"
                minFractionDigits={0}
                maxFractionDigits={2}
                onValueChange={e => void setFieldValue('goalSum', e.value)}
              />
              <ErrorMessage name="goalSum" component="div" className="p-error" />
            </div>
          </div>
        )}
      </>
    );
  }

  function getSystemDataFields(
    values: FormikValues,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
  ) {
    return (
      <>
        <Divider align="left">
          <div className="inline-flex align-items-center">
            <b>Systémové údaje</b>
          </div>
        </Divider>
        {showField('createdBy') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="createdBy">Vytvoril</label>
              <Field disabled={true} name="createdBy" as={InputText} />
              <ErrorMessage name="createdBy" component="div" className="p-error" />
            </div>
          </div>
        )}

        {showField('createdDate') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="createdDate">Vytvorené dňa</label>
              <InputText
                id="createdDate"
                value={values.createdDate ? formatDateFromInstant(values.createdDate) : ''}
                disabled={true}
              />
              <ErrorMessage name="createdDate" component="div" className="p-error" />
            </div>
          </div>
        )}
        {showField('lastModifiedBy') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="lastModifiedBy">Naposledy aktualizoval</label>
              <Field disabled={true} name="lastModifiedBy" as={InputText} />
            </div>
          </div>
        )}
        {showField('lastModifiedDate') && (
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="lastModifiedDate">Naposledy aktualizované dňa</label>
              <InputText
                id="lastModifiedDate"
                value={values.lastModifiedDate ? formatDateFromInstant(values.lastModifiedDate) : ''}
                disabled={true}
              />
              <ErrorMessage name="lastModifiedDate" component="div" className="p-error" />
            </div>
          </div>
        )}
      </>
    );
  }

  const onCiastocnyVyber = values => {
    setIsSavingCiastocnyVyber(true);
    sendCiastocnyVyber(values.id, { vyberPenazi: values.vyberPenazi, vyberPenaziKDatumu: values.vyberPenaziKDatumu })
      .then(response => {
        showSuccess('Úspech', 'Čiastočný výber bol nastavený.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setIsSavingCiastocnyVyber(false));
  };

  function getCiastocnyVyberFields(
    values: FormikValues,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
  ) {
    return (
      <>
        {showField('vyberPenazi') && (
          <>
            <Divider align="left">
              <div className="inline-flex align-items-center">
                <b>Čiastočný výber</b>
              </div>
            </Divider>
            <div className="col-12 md:col-4">
              <div className="p-field">
                <label htmlFor="vyberPenazi">Vybratá suma</label>
                <InputNumber
                  readOnly={readOnly}
                  id="vyberPenazi"
                  value={values.vyberPenazi}
                  locale="sk"
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  onValueChange={e => void setFieldValue('vyberPenazi', e.value)}
                />
                <ErrorMessage name="vyberPenazi" component="div" className="p-error" />
              </div>
            </div>
          </>
        )}

        {showField('vyberPenaziKDatumu') && (
          <div className="col-12 md:col-4">
            <div className="p-field">
              <label htmlFor="vyberPenaziKDatumu">Ku dňu</label>
              <DateTextInput
                id="vyberPenaziKDatumu"
                value={values.vyberPenaziKDatumu}
                onChange={val => void setFieldValue('vyberPenaziKDatumu', val)}
              />
              <ErrorMessage name="createdDate" component="div" className="p-error" />
            </div>
          </div>
        )}

        {showField('vyberPenaziKDatumu') && (
          <div className="col-12 md:col-4">
            <div className="p-field">
              <Button
                disabled={isSavingCiastocnyVyber}
                type="button"
                style={{ backgroundColor: '#e31818', marginTop: '20px' }}
                onClick={() => onCiastocnyVyber(values)}
                label="Uskutočniť čiastočný výber"
              />
            </div>
          </div>
        )}
      </>
    );
  }

  const handleFileUpload = event => {
    const files = event.files;

    setIsSaving(true);
    uploadDocument(contract?.id, files[0])
      .then(response => {
        if (prilohy) {
          setPrilohy(prev => [...prev, response.data]);
        } else {
          setPrilohy([response.data]);
        }
        if (fileUploadRef.current) {
          fileUploadRef.current.clear();
        }
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setIsSaving(false));
  };

  const renderHeader = () => {
    return (
      <>
        {!readOnly && (
          <div className="flex justify-content-between">
            <FileUpload
              ref={fileUploadRef}
              mode="basic"
              multiple={false}
              auto={true}
              name="files"
              accept=".csv,.xml,.pdf,.xlsx,.png,.jpg,.jpeg,.doc,.docx,.xls,.ppt,.pptx,.txt,.odt,.ods,.odp,.ics,.zip,.tiff"
              customUpload
              uploadHandler={handleFileUpload}
              chooseLabel="Nahrať prílohu"
              uploadLabel="Nahrať"
              cancelLabel="Zrušiť"
              disabled={isSaving}
            />{' '}
          </div>
        )}
      </>
    );
  };

  const header = renderHeader();

  const onDownloadDocument = data => {
    downloadDocument(data);
  };

  const confirmDeleteContact = documentToDelete => {
    setDeleteDocumentDialog(true);
    setDeleteDocument(documentToDelete);
  };

  const handleDeleteDocument = () => {
    setIsSaving(true);

    deleteDocumentById(deleteDocument)
      .then(response => {
        setPrilohy(prevState => prevState.filter(p => p.id !== response.data));
        setDeleteDocumentDialog(false);
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setIsSaving(false));
  };

  const deleteDocumentDialogFooter = (
    <React.Fragment>
      <Button style={{ backgroundColor: 'gray' }} label="Nie" icon="pi pi-times" outlined onClick={() => setDeleteDocumentDialog(false)} />
      <Button style={{ backgroundColor: '#e31818' }} label="Áno" icon="pi pi-check" severity="danger" onClick={handleDeleteDocument} />
    </React.Fragment>
  );

  const onPreviewDocument = data => {
    setPreviewDocument(data);
    setPreviewDocumentDialog(true);
  };

  const actionBodyDocumentsTemplate = rowData => {
    return (
      <React.Fragment>
        <div className="flex justify-content-around">
          <Button
            style={{ backgroundColor: '#e31818' }}
            icon="pi pi-eye"
            type="button"
            className="mr-2"
            tooltip="Náhľad"
            onClick={() => onPreviewDocument(rowData)}
          />
          <Button
            style={{ backgroundColor: '#e31818' }}
            icon="pi pi-download"
            type="button"
            className="mr-2"
            tooltip="Stiahnuť"
            onClick={() => onDownloadDocument(rowData)}
          />
          {!readOnly && (
            <Button style={{ backgroundColor: '#e31818' }} icon="pi pi-trash" type="button" onClick={() => confirmDeleteContact(rowData)} />
          )}
        </div>
      </React.Fragment>
    );
  };

  function getDocuments(
    values: FormikValues,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
  ) {
    return (
      <div className="col-12">
        <DataTable
          stripedRows
          value={prilohy}
          size="small"
          dataKey="id"
          header={header}
          emptyMessage="Žiadne prílohy"
          sortField="id"
          sortOrder={SortOrder.DESC}
        >
          <Column field="originalFilename" header="Názov súboru" />
          <Column field="fileSize" header="Veľkosť" body={v => v?.fileSize / 1000 + ' KB'} />
          <Column body={actionBodyDocumentsTemplate} header="Akcie" exportable={false} style={{ justifyContent: 'end' }}></Column>
        </DataTable>
      </div>
    );
  }

  function getCarFields(
    values: FormikValues,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
  ) {
    return (
      <>
        {showField('car') && (
          <>
            <Divider align="left">
              <div className="inline-flex align-items-center">
                <b>Údaje o aute</b>
              </div>
            </Divider>
            <div className="col-12 md:col-6">
              <div className="p-field">
                <label htmlFor="znacka">Značka</label>
                <Field readOnly={readOnly} name="znacka" as={InputText} />
                <ErrorMessage name="znacka" component="div" className="p-error" />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="p-field">
                <label htmlFor="model">Model</label>
                <Field readOnly={readOnly} name="model" as={InputText} />
                <ErrorMessage name="model" component="div" className="p-error" />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="p-field">
                <label htmlFor="spz">ŠPZ</label>
                <Field readOnly={readOnly} name="spz" as={InputText} />
                <ErrorMessage name="spz" component="div" className="p-error" />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="p-field">
                <label htmlFor="vin">VIN</label>
                <Field readOnly={readOnly} name="vin" as={InputText} />
                <ErrorMessage name="vin" component="div" className="p-error" />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="p-field">
                <label htmlFor="objem">Objem</label>
                <Field readOnly={readOnly} name="objem" as={InputText} />
                <ErrorMessage name="objem" component="div" className="p-error" />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="p-field">
                <label htmlFor="vykon">Výkon</label>
                <Field readOnly={readOnly} name="vykon" as={InputText} />
                <ErrorMessage name="vykon" component="div" className="p-error" />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="p-field">
                <label htmlFor="hmotnost">Hmotnosť</label>
                <Field readOnly={readOnly} name="hmotnost" as={InputText} />
                <ErrorMessage name="hmotnost" component="div" className="p-error" />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="p-field">
                <label htmlFor="farba">Farba</label>
                <Field readOnly={readOnly} name="farba" as={InputText} />
                <ErrorMessage name="farba" component="div" className="p-error" />
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <div className="card">
      {loading && (
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      )}
      {!loading && (
        <>
          <h2>{id ? 'Upraviť dáta zmluvy' : 'Vytvoriť novú zmluvu'}</h2>

          <div className="p-fluid">
            <div className="grid">
              <div className="col-12 md:col-6">
                <div className="p-field">
                  <label htmlFor="insuranceCompany">Spoločnosť</label>
                  <Dropdown
                    id="insuranceCompany"
                    disabled={readOnly || (!!id && !productEditable)}
                    name="insuranceCompany"
                    value={selectedCompany}
                    options={insuranceCompanies}
                    optionLabel="label"
                    emptyMessage="Spoločnosť neexistuje"
                    emptyFilterMessage="Spoločnosť neexistuje"
                    filter
                    onChange={e => {
                      setSelectedCompany(e.value);
                      setSelectedProduct(null);
                    }}
                    placeholder="Vyberte si spoločnosť"
                  />
                </div>
              </div>
              {selectedCompany && (
                <div className="col-12 md:col-6">
                  <div className="p-field">
                    <label htmlFor="product">Produkt</label>
                    <Dropdown
                      id="product"
                      name="product"
                      disabled={readOnly || (!!id && !productEditable)}
                      value={selectedProduct}
                      emptyMessage="Neexistujú žiadne produkty pre vybraný typ a spoločnosť"
                      emptyFilterMessage="Produkt neexistuje"
                      options={products}
                      filter
                      optionLabel="label"
                      onChange={e => void onProductSelected(e.value)}
                      placeholder="Vyberte si produkt"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <Formik
            initialValues={
              contract
                ? contract
                : {
                    product: '',
                    policyHolder: null,
                    paymentFrequency: null,
                    concludedBy: null,
                    payment: null,
                    yearlyPayment: null,
                    dateOfSignature: null,
                    contractNr: null,
                    proposalNr: null,
                  }
            }
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, setFieldValue, values, setFieldTouched, errors }) => {
              return (
                <Form onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} className="p-fluid" style={{ marginTop: '2rem' }}>
                  <div className="grid">
                    <TabView style={{ width: '100%' }} activeIndex={location.search?.includes('attachments') ? 1 : 0}>
                      <TabPanel header="Základné údaje">
                        <div className="grid">
                          {id && (
                            <div className="col-12 md:col-4">
                              <div className="p-field">
                                <label htmlFor="approved">Zmluva schválená: </label>
                                <Checkbox
                                  disabled={readOnly}
                                  name="approved"
                                  onChange={e => void setFieldValue('approved', e.checked)}
                                  checked={values.approved}
                                ></Checkbox>
                                <ErrorMessage name="approved" component="div" className="p-error" />
                              </div>
                            </div>
                          )}
                          {id && structure?.fields?.length > 0 && (
                            <div className={id ? 'col-12 md:col-4' : 'col-6'}>
                              <div className="p-field">
                                <label htmlFor="state">Stav zmluvy</label>
                                <Dropdown
                                  disabled={readOnly}
                                  id="state"
                                  name="state"
                                  value={values.state?.id}
                                  options={contractStates}
                                  optionLabel="label"
                                  onChange={e => void setFieldValue('state.id', e.value)}
                                  placeholder="Vyberte si stav zmluvy"
                                />
                                <ErrorMessage name="state" component="div" className="p-error" />
                              </div>
                            </div>
                          )}
                          {id && structure?.fields?.length > 0 && values.state?.id === 5 && (
                            <div className="col-12 md:col-4">
                              <div className="p-field">
                                <label htmlFor="datumStorna">Dátum storna</label>
                                <DateTextInput
                                  readOnly={readOnly}
                                  id="datumStorna"
                                  value={values.datumStorna}
                                  onChange={val => void setFieldValue('datumStorna', val)}
                                />
                                <ErrorMessage name="datumStorna" component="div" className="p-error" />
                              </div>
                            </div>
                          )}
                          {structure?.fields?.length > 0 && (
                            <div className={id && values.state?.id !== 5 ? 'col-12 md:col-4' : 'col-12'}>
                              <div className="p-field">
                                <label htmlFor="dispatchNoteNumber">Číslo sprievodky</label>
                                <Dropdown
                                  id="dispatchNoteNumber"
                                  name="dispatchNoteNumber"
                                  disabled={readOnly}
                                  value={values.dispatchNoteNumber ?? null}
                                  options={dispatchNotes}
                                  optionLabel="label"
                                  // showClear={true}
                                  onChange={e => {
                                    setFieldValue('dispatchNoteNumber', e.value);
                                  }}
                                  placeholder="Vyberte si číslo sprievodky"
                                />

                                <ErrorMessage name="dispatchNoteNumber" component="div" className="p-error" />
                              </div>
                            </div>
                          )}
                          {structure?.fields?.length > 0 && getPersonalFields(values, setFieldValue)}
                          {structure?.fields?.length > 0 && getGeneralFields(values, setFieldValue)}
                          {structure?.fields?.length > 0 && getCarFields(values, setFieldValue)}

                          {structure?.fields?.length > 0 && id && getCiastocnyVyberFields(values, setFieldValue)}

                          <div className="col-12 field">
                            <label htmlFor="note" className="text-900 font-semibold">
                              Poznámka
                            </label>
                            <InputTextarea
                              id="note"
                              readOnly={readOnly}
                              value={values.note ?? ''}
                              onChange={e => void setFieldValue('note', e.target.value)}
                              rows={4}
                              autoResize
                              style={{ width: '100%' }}
                            />
                          </div>
                        </div>
                      </TabPanel>
                      <TabPanel header="Prílohy k zmluve">
                        <div className="grid">{structure?.fields?.length > 0 && id && getDocuments(values, setFieldValue)}</div>
                      </TabPanel>
                      {isAdmin && (
                        <TabPanel disabled={!id} header="Platby">
                          <div className="grid">
                            <CreateContractPayments id={id} readOnly={readOnly} />
                          </div>
                        </TabPanel>
                      )}
                      {isAdminOrAssistant && (
                        <TabPanel disabled={!id} header="Zmeny v zmluve">
                          <div className="grid">
                            <ContractHistoryTable contractId={id} />
                          </div>
                        </TabPanel>
                      )}
                      {canViewProvisionSheets && (
                        <TabPanel disabled={!id} header="Provízne zostavy">
                          <DataTable
                            stripedRows
                            value={contractProvisionSheets}
                            size="small"
                            responsiveLayout="scroll"
                            dataKey="provisionSheetId"
                            emptyMessage="Zmluva sa nenachádza v žiadnych províznych zostavách."
                            loading={provisionSheetsLoading}
                          >
                            <Column field="taxDocumentNr" header="Číslo dokladu" />
                            <Column field="agentName" header="Obchodník" />
                            <Column field="positionName" header="Pozícia" />
                            <Column
                              field="setFrom"
                              header="Obdobie od"
                              body={row => (row.setFrom ? formatFromLocalDate(row.setFrom) : '-')}
                            />
                            <Column
                              field="setTo"
                              header="Obdobie do"
                              body={row => (row.setTo ? formatFromLocalDate(row.setTo) : '-')}
                            />
                            <Column
                              field="provision"
                              header="Suma"
                              body={row => (
                                <span style={{ color: row.isStorno ? 'red' : 'inherit' }}>
                                  {row.provision} {row.isStorno ? '(storno)' : ''}
                                </span>
                              )}
                            />
                          </DataTable>
                        </TabPanel>
                      )}
                    </TabView>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px' }}>
                    <Button
                      style={{ backgroundColor: 'gray' }}
                      type="button"
                      label="Späť"
                      icon="pi pi-arrow-left"
                      className="p-button-secondary"
                      onClick={goBack}
                    />
                    {!readOnly && (
                      <Button style={{ backgroundColor: '#e31818' }} type="submit" label="Uložiť" icon="pi pi-check" disabled={isSaving} />
                    )}
                  </div>
                  <PaymentTemplateHistoryDialog
                    visible={paymentTemplateHistoryShow}
                    onSave={() => onSavePaymentTemplateHistory(values)}
                    onHide={() => setPaymentTemplateHistoryShow(false)}
                    payments={values?.contractPaymentTemplates}
                    setPayments={e => void setFieldValue('contractPaymentTemplates', e)}
                    paymentFrequencies={paymentFrequencies}
                    getYearlyPayment={getYearlyPayment}
                    isSaving={isSaving}
                    readOnly={readOnly}
                  />
                </Form>
              );
            }}
          </Formik>
        </>
      )}
      <Dialog
        visible={productChangeDialogVisible}
        style={{ width: '36rem' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        header="Zmena produktu"
        modal
        footer={
          <React.Fragment>
            <Button style={{ backgroundColor: 'gray' }} label="Zrušiť" icon="pi pi-times" outlined onClick={cancelProductChange} />
            <Button style={{ backgroundColor: '#e31818' }} label="Pokračovať" icon="pi pi-check" onClick={confirmProductChangeAndSave} />
          </React.Fragment>
        }
        onHide={cancelProductChange}
      >
        <div className="confirmation-content" style={{ display: 'flex', alignItems: 'flex-start' }}>
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
          <span>
            Menite produkt na tejto zmluve. Pripoistenia tejto zmluvy budú odstránené (sú naviazané na pôvodný produkt)
            a údaje špecifické pre pôvodný typ produktu, ktoré nie sú relevantné pre nový produkt, budú vymazané. Chcete pokračovať?
          </span>
        </div>
      </Dialog>
      <Dialog
        visible={deleteDocumentDialog}
        style={{ width: '32rem' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        header="Potvrdenie"
        modal
        footer={deleteDocumentDialogFooter}
        onHide={() => setDeleteDocumentDialog(false)}
      >
        <div className="confirmation-content">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
          {deleteDocument && (
            <span>
              Naozaj chcete vymazať prílohu: <b>{deleteDocument.originalFilename}</b>?
            </span>
          )}
        </div>
      </Dialog>
      <DocumentPreviewDialog
        visible={previewDocumentDialog}
        onHide={() => setPreviewDocumentDialog(false)}
        document={previewDocument}
      />
    </div>
  );
};

export default CreateContract;
